// path: frontend/src/lib/firebase.ts
// ========================================================================
// Firebase Configuration - Production Ready ✅
// Key point for Render-hosted frontend:
// - DO NOT set authDomain to your Render custom domain unless Firebase Hosting
//   is serving /__/auth/* on that same domain.
// - Use the Firebase auth domain: <project-id>.firebaseapp.com
// ========================================================================

import { initializeApp } from "firebase/app";
import {
  getAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  setPersistence,
  onAuthStateChanged,
  type User,
} from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";

// ------------------------------------------------------------------------
// Environment variables (Vite)
// ------------------------------------------------------------------------
const env = (import.meta as any).env ?? {};

const VITE_FIREBASE_API_KEY = env.VITE_FIREBASE_API_KEY;
const VITE_FIREBASE_AUTH_DOMAIN = env.VITE_FIREBASE_AUTH_DOMAIN; // optional override
const VITE_FIREBASE_PROJECT_ID = env.VITE_FIREBASE_PROJECT_ID;
const VITE_FIREBASE_APP_ID = env.VITE_FIREBASE_APP_ID;

const VITE_USE_FIREBASE_EMULATORS = env.VITE_USE_FIREBASE_EMULATORS;
const VITE_EMULATOR_AUTH_HOST = env.VITE_EMULATOR_AUTH_HOST;
const VITE_EMULATOR_AUTH_PORT = env.VITE_EMULATOR_AUTH_PORT;
const VITE_EMULATOR_FIRESTORE_HOST = env.VITE_EMULATOR_FIRESTORE_HOST;
const VITE_EMULATOR_FIRESTORE_PORT = env.VITE_EMULATOR_FIRESTORE_PORT;
const VITE_EMULATOR_FUNCTIONS_HOST = env.VITE_EMULATOR_FUNCTIONS_HOST;
const VITE_EMULATOR_FUNCTIONS_PORT = env.VITE_EMULATOR_FUNCTIONS_PORT;

// ------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------
function normalizeOrigin(s: string): string {
  return String(s || "").trim().replace(/\/+$/, "");
}

function getAuthDomain(projectId: string): string {
  // Priority:
  // 1) Explicit env override (if you know exactly what you're doing)
  // 2) Default Firebase Hosting/Auth domain (works with Render custom domain sites)
  const override = normalizeOrigin(VITE_FIREBASE_AUTH_DOMAIN || "");
  if (override) return override;

  // Firebase Auth expects this domain to serve /__/auth/*
  return `${projectId}.firebaseapp.com`;
}

// ------------------------------------------------------------------------
// Validate required env (production should have these set)
// ------------------------------------------------------------------------
const projectId = String(VITE_FIREBASE_PROJECT_ID || "").trim();
if (!projectId) {
  // Allow dev fallback, but production must set projectId
  console.warn("⚠️ Missing VITE_FIREBASE_PROJECT_ID. Firebase will not work correctly.");
}

const firebaseConfig = {
  apiKey: String(VITE_FIREBASE_API_KEY || "demo-key"),
  authDomain: getAuthDomain(projectId || "demo-autoquiz-local"),
  projectId: projectId || "demo-autoquiz-local",
  appId: String(VITE_FIREBASE_APP_ID || "demo-app-id"),
};

// Log in dev only
if (import.meta.env.DEV) {
  console.info("🔧 Firebase Config:", {
    projectId: firebaseConfig.projectId,
    authDomain: firebaseConfig.authDomain,
    mode: import.meta.env.MODE,
  });
}

// ------------------------------------------------------------------------
// Initialize
// ------------------------------------------------------------------------
const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);

// Persist login
setPersistence(auth, browserLocalPersistence).catch((error) => {
  if (import.meta.env.DEV) {
    console.warn("Failed to set auth persistence:", error);
  }
});

// ------------------------------------------------------------------------
// Emulators (DEV ONLY)
// ------------------------------------------------------------------------
const isDevBuild = import.meta.env.DEV === true;
const useEmulators = isDevBuild && String(VITE_USE_FIREBASE_EMULATORS ?? "1") === "1";

if (useEmulators) {
  const authHost = String(VITE_EMULATOR_AUTH_HOST || "127.0.0.1");
  const authPort = Number(VITE_EMULATOR_AUTH_PORT || 9099);

  const fsHost = String(VITE_EMULATOR_FIRESTORE_HOST || "127.0.0.1");
  const fsPort = Number(VITE_EMULATOR_FIRESTORE_PORT || 8080);

  const fnHost = String(VITE_EMULATOR_FUNCTIONS_HOST || "127.0.0.1");
  const fnPort = Number(VITE_EMULATOR_FUNCTIONS_PORT || 5001);

  connectAuthEmulator(auth, `http://${authHost}:${authPort}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, fsHost, fsPort);
  connectFunctionsEmulator(functions, fnHost, fnPort);

  if (typeof window !== "undefined") {
    (window as any).__usingFirebaseEmulators = {
      auth: `http://${authHost}:${authPort}`,
      firestore: `http://${fsHost}:${fsPort}`,
      functions: `http://${fnHost}:${fnPort}`,
    };
  }

  console.info("🔧 Firebase Emulators Connected:", {
    auth: `http://${authHost}:${authPort}`,
    firestore: `http://${fsHost}:${fsPort}`,
    functions: `http://${fnHost}:${fnPort}`,
  });
}

// ------------------------------------------------------------------------
// Small utilities (optional, useful for API calls)
// ------------------------------------------------------------------------
export async function getCurrentUserToken(forceRefresh = false): Promise<string | null> {
  const u = auth.currentUser;
  if (!u) return null;
  try {
    return await u.getIdToken(forceRefresh);
  } catch (err) {
    if (import.meta.env.DEV) console.warn("Failed to get ID token:", err);
    return null;
  }
}

export function isAuthenticated(): boolean {
  return auth.currentUser !== null;
}

export function getCurrentUser(): User | null {
  return auth.currentUser;
}

export function onAuthChange(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth, callback);
}

export default app;

